import { repApi } from './repApi';
import { FOLDER_MIME, LEGACY_BUCKET } from '@pages/FileLibrary/constants';
import type { RootState } from '@store/index';
import type { Docket, UpsertDocketBody } from '@/types';

/**
 * Scoped list queries. The docket table is far too large to load whole (the
 * full listing exceeded Lambda's 6 MB response cap), so the client asks for:
 *  - 'folders': every folder row (small set - drives breadcrumbs, visibility
 *    and the move dialog tree)
 *  - 'folder': one folder's contents (folderId 0 = root), matched server-side
 *    on the legacy dual linkage (pvParentID OR cbrnDataFileSequenceID)
 *  - 'search': server-side name search
 */
export type DocketListScope =
  { kind: 'folders' } | { kind: 'folder'; folderId: number } | { kind: 'search'; term: string };

const scopeToFilter = (scope: DocketListScope): string => {
  switch (scope.kind) {
    case 'folders':
      return JSON.stringify({ where: { cbrnMIMEContentCodeSimpleType: FOLDER_MIME } });
    case 'folder':
      return JSON.stringify({ where: { folderId: scope.folderId } });
    case 'search':
      return JSON.stringify({ where: { cbrnDataFileName: { like: `%${scope.term}%` } } });
  }
};

/**
 * The download route for a stored key. REP rows hold the legacy S3 key
 * (`<timestamp>__<file name>` in the legacy bucket); older rows may hold the
 * files-api form `/api/containers/<bucket>/download/<key>`, which this API
 * serves under its /api/rep base.
 */
export const docketPath = (uri: string) =>
  uri.startsWith('/api/containers/')
    ? uri.replace(/^\/api\//, '/')
    : `/containers/${LEGACY_BUCKET}/download/${encodeURIComponent(uri)}`;

export interface FolderCount {
  folderId: number;
  count: number;
}

/**
 * The docket (File Library) endpoints of palmetto-typescript-rep-api, ported
 * from palmetto-typescript-filelibrary's docketsApi and injected into the
 * shared repApi (same base URL and id_token Authorization header).
 */
export const docketsApi = repApi.injectEndpoints({
  endpoints: (builder) => ({
    listDockets: builder.query<Docket[], DocketListScope>({
      query: (scope) => ({
        url: '/dockets',
        params: { filter: scopeToFilter(scope) },
      }),
    }),
    /** Per-folder file counts (one aggregate row per folder). */
    listDocketCounts: builder.query<FolderCount[], void>({
      query: () => ({ url: '/dockets/counts' }),
      providesTags: ['DocketCounts'],
    }),
    upsertDocket: builder.mutation<Docket, UpsertDocketBody>({
      query: (body) => ({ url: '/dockets', method: 'PUT', body }),
      // The client already knows the change it made: on success, merge the
      // request fields (over the response record) straight into every cached
      // list slice it belongs to. No list refetch - the 60s polls reconcile
      // server-derived columns in the background.
      async onQueryStarted(body, { dispatch, getState, queryFulfilled }) {
        try {
          const { data: saved } = await queryFulfilled;
          const known = Object.fromEntries(
            Object.entries(body).filter(([, value]) => value !== undefined),
          );
          const account = (getState() as unknown as RootState).app.account;
          const currentFullName =
            [account?.pvPersonGivenName, account?.pvPersonSurName].filter(Boolean).join(' ') ||
            undefined;
          dispatch(
            applyDocketToListCache({
              ...saved,
              ...known,
              pvDataID: saved?.pvDataID ?? body.pvDataID,
              pvUserName: saved?.pvUserName || account?.username || undefined,
              pvFullName: saved?.pvFullName || currentFullName,
            } as Docket),
          );
        } catch {
          // Mutation failed - nothing to patch.
        }
      },
      invalidatesTags: ['DocketCounts'],
    }),
    /** Soft-deletes each id (pvVoid = 1); the API has no bulk endpoint. */
    voidDockets: builder.mutation<{ deleted: number[] }, number[]>({
      queryFn: async (ids, _api, _extra, baseQuery) => {
        for (const pvDataID of ids) {
          const response = await baseQuery({
            url: '/dockets/delete',
            method: 'PUT',
            body: { pvDataID },
          });
          if (response.error) {
            return { error: response.error };
          }
        }
        return { data: { deleted: ids } };
      },
      // Same principle as upsert: the deleted ids are known - drop them from
      // every cached list slice on success instead of refetching.
      async onQueryStarted(ids, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
          for (const scope of docketsApi.util.selectCachedArgsForQuery(getState(), 'listDockets')) {
            dispatch(
              docketsApi.util.updateQueryData('listDockets', scope, (draft) => {
                for (const pvDataID of ids) {
                  const index = draft.findIndex((docket) => docket.pvDataID === pvDataID);
                  if (index >= 0) draft.splice(index, 1);
                }
              }),
            );
          }
        } catch {
          // Mutation failed - nothing to patch.
        }
      },
      invalidatesTags: ['DocketCounts'],
    }),
    /**
     * Resolves a presigned S3 URL for a stored cbrnDataFileURIID. An <a href>
     * cannot carry the Authorization header, so the API returns { url } JSON
     * with ?redirect=false and the client opens that URL.
     */
    downloadUrl: builder.query<{ url: string }, string>({
      query: (cbrnDataFileURIID) => ({
        url: `${docketPath(cbrnDataFileURIID)}${cbrnDataFileURIID.includes('?') ? '&' : '?'}redirect=false`,
      }),
      keepUnusedDataFor: 0,
    }),
    /**
     * Mints a shareable link for a file, valid for 7 days. The returned url is
     * a SHORT link (/api/link/{shortId}, emsmob-style, stored in the palmetto
     * link table) that redirects to the API's signed share endpoint, which in
     * turn redirects to a fresh presigned S3 URL.
     */
    createShareLink: builder.mutation<
      { url: string; expiresAt: string; expiresInDays: number },
      number
    >({
      query: (pvDataID) => ({ url: `/dockets/${pvDataID}/share-link`, method: 'POST' }),
    }),
  }),
});

export const {
  useListDocketsQuery,
  useListDocketCountsQuery,
  useUpsertDocketMutation,
  useVoidDocketsMutation,
  useLazyDownloadUrlQuery,
  useCreateShareLinkMutation,
} = docketsApi;

const isFolderRow = (docket: Docket) => docket.cbrnMIMEContentCodeSimpleType === FOLDER_MIME;

const belongsToFolder = (docket: Docket, folderId: number) => {
  if (folderId === 0) {
    return !docket.pvParentID && !docket.cbrnDataFileSequenceID;
  }
  return docket.pvParentID === folderId || docket.cbrnDataFileSequenceID === folderId;
};

/**
 * Thunk: upsert a docket record into every cached list slice it belongs to
 * (and remove it from folder slices it no longer belongs to, e.g. after a
 * move). Used by the mutations above and the upload hook.
 */
export const applyDocketToListCache =
  (docket: Docket | null | undefined) =>
  (dispatch: (action: unknown) => unknown, getState: () => unknown) => {
    if (!docket?.pvDataID) return;
    for (const scope of docketsApi.util.selectCachedArgsForQuery(
      getState() as Parameters<typeof docketsApi.util.selectCachedArgsForQuery>[0],
      'listDockets',
    )) {
      const matches =
        scope.kind === 'folders'
          ? isFolderRow(docket)
          : scope.kind === 'folder'
            ? belongsToFolder(docket, scope.folderId)
            : displayNameMatches(docket, scope.term);
      dispatch(
        docketsApi.util.updateQueryData('listDockets', scope, (draft) => {
          const index = draft.findIndex((row) => row.pvDataID === docket.pvDataID);
          if (matches) {
            if (index >= 0) {
              draft[index] = { ...draft[index], ...docket };
            } else {
              draft.push(docket);
            }
          } else if (index >= 0 && scope.kind === 'folder') {
            // Moved elsewhere - drop it from this folder's slice.
            draft.splice(index, 1);
          }
        }),
      );
    }
  };

// Mirrors the server-side search rule: file/folder name OR owner display name.
const displayNameMatches = (docket: Docket, term: string) =>
  (docket.cbrnDataFileName || '').toLowerCase().includes(term.toLowerCase()) ||
  (docket.pvFullName || '').toLowerCase().includes(term.toLowerCase());
